import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowUpIcon } from "@heroicons/react/24/solid";

function BlogDetail() {
  const { id } = useParams(); // Get the ID from the URL
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch the blog post by ID
    fetch(`${process.env.REACT_APP_API_URL}/api/posts/${id}`)
      .then((response) => response.json())
      .then((data) => {
        setPost(data.post); // Assuming the data structure contains `post`
        setLoading(false);
      })
      .catch((error) => {
        setError("Error fetching blog post.");
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen  ">
        <div className="loader animate-spin rounded-full border-4 border-t-4 border-blue-500 w-16 h-16"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen text-center">
        <h2 className="text-2xl font-bold text-red-500">Error: {error}</h2>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      <div className="h-3 bg-slate-900"></div>
      <div className="max-w-5xl mx-auto p-6 md:p-12 bg-slate-300 rounded-xl shadow-2xl">
        {/* Heading */}
        <h1 className="text-3xl font-extrabold text-blue-900 text-center mb-8 leading-tight tracking-wide">
          {post?.heading || "Untitled Blog"}
        </h1>
        {/* Tags Section */}
        {post?.tags?.length > 0 && (
          <div className="mt-2 mb-5 flex flex-wrap gap-2 justify-center md:justify-start">
            {post.tags.map((tags, index) => (
              <span
                key={index}
                className="bg-gray-200 text-gray-800 text-sm font-semibold px-3 py-1 rounded-full"
              >
                #{tags}
              </span>
            ))}
          </div>
        )}
        {/* Content Preview */}
        <div className="prose lg:prose-base max-w-none text-gray-700 mb-8 leading-relaxed">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {post?.content || "Nothing to preview..."}
          </ReactMarkdown>
        </div>

        {/* Additional Info */}
        <div className="mt-4 border-t border-gray-700 pt-6 text-sm text-gray-500">
          {/* Author Details */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-4 md:mb-6">
            <p className="mb-2 md:mb-0  font-bold text-black text-center md:text-left">
              Written by:{" "}
              <span className="font-medium text-blue-800">
                {post?.username || "Unknown User"}
              </span>
            </p>
            {/* <p className="text-center font-bold text-black md:text-right">
              Email:{" "}
              <a
                href={`mailto:${post?.email}`}
                className="text-blue-600 underline hover:text-blue-800"
              >
                {post?.email || "N/A"}
              </a>
            </p> */}
          </div>

          {/* Post Metadata */}
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="flex items-center gap-2 mb-2 md:mb-0">
              <span className="font-semibold text-gray-800">Posted on:</span>{" "}
              {post?.createdAt
                ? new Date(post.createdAt).toLocaleString()
                : "Unknown Date"}
            </p>
            <p className="flex items-center gap-2">
              <span className="text-red-500">❤️</span>
              <span className="font-semibold text-gray-800">
                Total Reactions:
              </span>{" "}
              <span className="text-blue-700 font-bold">
                {post?.totalReactions || 0}
              </span>
            </p>
          </div>
        </div>

        {/* Back to Top Button */}
        <div className="mt-10 text-center">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white font-bold shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-400 focus:ring-opacity-50 transition-transform transform hover:scale-110"
            aria-label="Back to top"
          >
            <ArrowUpIcon className="w-6 h-6" />
          </button>
        </div>
      </div>
      <div className="h-3 bg-slate-900"></div>
    </div>
  );
}

export default BlogDetail;
